import { MouseEvent, FC, useCallback, useMemo } from 'react';
import isEmail from 'validator/es/lib/isEmail';
import { TextLink, ConfirmationModal, useModalControl } from '@frontend/design-system';
import { highlightSegment } from './highlight-segment';
import { formatUrlWithProtocol } from './link-parsing.utils';
import { EmailLinkProps, UrlLinkProps, LinkComponentProps } from './types';

/**
 * Renders an email link with mailto: protocol
 *
 * @example
 * <EmailLink
 *   text="support@example.com"
 *   index={0}
 *   subject="Support Request"
 *   className="email-link"
 * />
 */
export const EmailLink: FC<EmailLinkProps> = ({
  text,
  index,
  className = '',
  style,
  onClick,
  subject,
  body,
  highlightedText,
  ...rest
}) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(`mailto:${text}`);
    }
  }, [onClick, text]);

  // Build mailto URL with optional parameters
  const href = useMemo(() => {
    const mailtoParams = new URLSearchParams();
    if (subject) mailtoParams.append('subject', subject);
    if (body) mailtoParams.append('body', body);
    const paramString = mailtoParams.toString();
    return `mailto:${text}${paramString ? `?${paramString}` : ''}`;
  }, [body, subject, text]);

  return (
    <TextLink
      as='a'
      key={`email-${text}-${index}`}
      href={href}
      className={className}
      style={style}
      onClick={handleClick}
      {...rest}
    >
      {highlightedText ? highlightSegment(text, highlightedText) : text}
    </TextLink>
  );
};

/**
 * Renders a URL link that opens in a new tab by default
 *
 * @example
 * <UrlLink
 *   text="https://example.com"
 *   index={0}
 *   className="url-link"
 *   openInNewTab={true}
 * />
 */
export const UrlLink: FC<UrlLinkProps> = ({
  text,
  index,
  className = '',
  style,
  onClick,
  openInNewTab = true,
  highlightedText,
  ...rest
}) => {
  const formattedUrl = formatUrlWithProtocol(text);
  const { modalProps, triggerProps } = useModalControl();

  const onClickConfirmation = () => {
    if (onClick) {
      onClick(formattedUrl);
    }
    if (rest.confirmationModalProps?.onConfirm) {
      rest.confirmationModalProps.onConfirm();
    }
    if (openInNewTab) {
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.open(formattedUrl, '_self', 'noopener,noreferrer');
    }
  };

  const onClickHandler = rest.confirmationModalProps
    ? (e: MouseEvent) => {
        e.preventDefault();
        triggerProps.onClick(e);
      }
    : undefined;

  return (
    <>
      <TextLink
        as='a'
        key={`url-${text}-${index}`}
        href={formattedUrl}
        className={className}
        style={style}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={onClickHandler}
        {...rest}
      >
        {highlightedText ? highlightSegment(text, highlightedText) : text}
      </TextLink>
      {rest.confirmationModalProps && (
        <ConfirmationModal {...rest.confirmationModalProps} {...modalProps} onConfirm={onClickConfirmation} />
      )}
    </>
  );
};

/**
 * Renders appropriate link component based on content type (email vs URL)
 *
 * @example
 * <SmartLink
 *   text="user@example.com"
 *   index={0}
 *   className="smart-link"
 * />
 *
 * <SmartLink
 *   text="https://example.com"
 *   index={1}
 *   className="smart-link"
 * />
 */
export const SmartLink: FC<
  LinkComponentProps & {
    emailProps?: Partial<EmailLinkProps>;
    urlProps?: Partial<UrlLinkProps>;
  }
> = ({ text, index, className, style, onClick, highlightedText, emailProps = {}, urlProps = {}, ...rest }) => {
  if (isEmail(text)) {
    return (
      <EmailLink
        text={text}
        index={index}
        className={className}
        style={style}
        onClick={onClick}
        highlightedText={highlightedText}
        {...emailProps}
        {...rest}
      />
    );
  }

  return (
    <UrlLink
      text={text}
      index={index}
      className={className}
      style={style}
      onClick={onClick}
      highlightedText={highlightedText}
      {...urlProps}
      {...rest}
    />
  );
};
